import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { describeSeat, seatBadgeClass } from '../linkedin-seat.js';
import type { ChannelFallback, SalesRep, SalesRepAssignment } from './types.js';

export interface WizCampaignTypeProps {
  emailAccountsByAie: Record<string, string[]>;
  linkedinAccountIdsByAie: Record<string, string[]>;
  aiEmployeeAssignments: SalesRepAssignment[];
  crmAutoSync: boolean;
  /**
   * False while an existing campaign's real data (including its actual
   * `aiEmployeeAssignments`) is still being fetched by the wizard's own load
   * effect. `aiEmployeeAssignments` reads as `[]` for that entire window,
   * indistinguishable from a genuinely-unassigned campaign — the auto-default
   * effect below must not act until this is true, or it can race the load and
   * silently pick an arbitrary default Sales Rep, on the live campaign, in
   * place of the real one. Always true for a brand-new draft (nothing to wait
   * for). See the effect's own comment for the full history.
   */
  assignmentsReady: boolean;
  /**
   * Channels the campaign's sequence actually sends on. A Sales Rep needs at
   * least one account for each of them, so the picker marks the ones still
   * missing instead of leaving it to a rejected save. Defaults to "neither
   * channel required", which is right for a fresh draft with no steps yet.
   */
  requiredChannels?: { email: boolean; linkedin: boolean };
  /**
   * Per-rep "borrow someone else's accounts for this channel" rules picked in
   * the Summary step. A rep covered by one is not missing an account, so the
   * hints below must stay quiet for them.
   */
  channelFallbacks?: ChannelFallback[];
  /**
   * Whether the per-rep "missing account" hints (and the info border that goes
   * with them) may show yet. Held false until the user actually tries to leave
   * the step (LinkedIn-UAT follow-up 2026-09-19): a rep just ticked has had no
   * chance to pick an account, and lighting a hint the instant they are checked
   * reads as being scolded for a change three seconds from finishing. The
   * wizard's goToStep flips this on when a forward move is blocked on a missing
   * account — the same push-forward the error toast always waited for. This only
   * gates DISPLAY; the real, server-mirrored gate in campaign-wizard is
   * unchanged. Defaults false. See [[feedback-validation-ux-hint-then-error]].
   */
  revealAccountGaps?: boolean;
  onEmailAccountsByAieChange: (next: Record<string, string[]>) => void;
  onLinkedinAccountIdsByAieChange: (next: Record<string, string[]>) => void;
  /**
   * `seed` carries account buckets that must land in the SAME update as the
   * assignment change (pre-selecting a just-checked rep's only account) — a
   * second setter call would spread a draft that predates this one.
   */
  onAssignmentsChange: (
    next: SalesRepAssignment[],
    seed?: { emailAccountsByAie?: Record<string, string[]>; linkedinAccountIdsByAie?: Record<string, string[]> },
  ) => void;
  onCrmAutoSyncChange: (enabled: boolean) => void;
}

interface EmailAccountWithAie {
  email: string;
  name: string;
  aiEmployeeId: string | null;
}
interface LinkedinAccountWithAie {
  id: string;
  name: string;
  status?: string;
  aiEmployeeId: string | null;
  /** Provider-reported seat features. Absent means unobserved — see linkedin-seat.ts. */
  premiumFeatures?: string[] | null;
}

/** Distribute 100% as evenly as possible across N integer buckets. */
const evenSplit = (n: number): number[] => {
  if (n <= 0) return [];
  const base = Math.floor(100 / n);
  const remainder = 100 - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0));
};

export const WizCampaignType = stateful((props: WizCampaignTypeProps) => {
  const {
    emailAccountsByAie,
    linkedinAccountIdsByAie,
    aiEmployeeAssignments,
    crmAutoSync,
    assignmentsReady,
    requiredChannels = { email: false, linkedin: false },
    channelFallbacks = [],
    revealAccountGaps = false,
    onEmailAccountsByAieChange,
    onLinkedinAccountIdsByAieChange,
    onAssignmentsChange,
    onCrmAutoSyncChange,
  } = props;

  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [salesRepsLoaded, setSalesRepsLoaded] = useState(false);
  const [loadingSalesReps, setLoadingSalesReps] = useState(false);

  const [allEmailAccounts, setAllEmailAccounts] = useState<EmailAccountWithAie[]>([]);
  const [allLinkedinAccounts, setAllLinkedinAccounts] = useState<LinkedinAccountWithAie[]>([]);
  const [emailLoaded, setEmailLoaded] = useState(false);
  const [linkedinLoaded, setLinkedinLoaded] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingLinkedin, setLoadingLinkedin] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [linkedinError, setLinkedinError] = useState('');

  // Guards the auto-default effect below so it only ever fires once — see
  // that effect's comment for why the default-pick can't live in here.
  const [autoAssignedDefault, setAutoAssignedDefault] = useState(false);

  const fetchSalesReps = async () => {
    if (salesRepsLoaded) return;
    setLoadingSalesReps(true);
    try {
      const resp = await fetch('/api/ai-employees');
      const data = (await resp.json()) as { ok: boolean; aiEmployees?: SalesRep[] };
      if (data.ok) setSalesReps(data.aiEmployees ?? []);
    } catch {
      /* ignore */
    }
    setSalesRepsLoaded(true);
    setLoadingSalesReps(false);
  };

  const fetchEmailAccounts = async () => {
    if (emailLoaded) return;
    setLoadingEmail(true);
    setEmailError('');
    try {
      // Two sources, one list — the same merge Settings does. Instantly is the
      // retiring path; a Unipile mailbox is the new one and must show here even
      // when Instantly is unconfigured or its plan lapsed (which makes
      // /api/accounts/email answer non-ok), or an email Sales Rep looks unowned.
      const [instantlyRes, unipileRes] = await Promise.all([
        fetch('/api/accounts/email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
        fetch('/api/accounts/unipile-email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
      ]);
      const instantly: EmailAccountWithAie[] = instantlyRes.ok ? (instantlyRes.accounts ?? []) : [];
      // A Unipile mailbox is keyed by the provider's account id, not an address
      // — the same id the launch reads as the Sending Account.
      const unipile: EmailAccountWithAie[] = unipileRes.ok
        ? (unipileRes.accounts ?? []).map((a: any) => ({
            email: a.id,
            name: a.email ?? a.displayName ?? a.id,
            aiEmployeeId: a.aiEmployeeId ?? null,
          }))
        : [];
      setAllEmailAccounts([...instantly, ...unipile]);
      setEmailLoaded(true);
      if (!instantlyRes.ok && !unipileRes.ok) setEmailError('Failed to load accounts');
    } catch (e) {
      setEmailError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoadingEmail(false);
    }
  };

  const fetchLinkedinAccounts = async () => {
    if (linkedinLoaded) return;
    setLoadingLinkedin(true);
    setLinkedinError('');
    try {
      const resp = await fetch('/api/accounts/linkedin');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = (await resp.json()) as { ok: boolean; accounts: LinkedinAccountWithAie[]; error?: string };
      if (!data.ok) throw new Error(data.error ?? 'Failed to load accounts');
      setAllLinkedinAccounts(data.accounts);
      setLinkedinLoaded(true);
    } catch (e) {
      setLinkedinError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoadingLinkedin(false);
    }
  };

  useEffect(() => {
    void fetchSalesReps();
  }, []);

  // Auto-pick a default Sales Rep — but only for a genuinely empty selection,
  // re-evaluated on the LATEST aiEmployeeAssignments every render (not a
  // value frozen at mount). An earlier version decided this inside
  // fetchSalesReps, a one-shot `useEffect(..., [])` callback that closed over
  // aiEmployeeAssignments as it was AT MOUNT — for an edit-mode load, that's
  // always `[]` (the real assignment arrives moments later, asynchronously,
  // from the campaign's own load effect), so it unconditionally overwrote an
  // already-launched campaign's real Sales Rep with an arbitrary default the
  // instant /api/ai-employees resolved. That was fixed to re-evaluate on every
  // render instead of once at mount — but `assignmentsReady` was still
  // missing: this effect fires as soon as BOTH `/api/ai-employees` (this
  // component's own fetch) AND the parent's separate campaign-load fetch
  // happen to have empty/not-yet-arrived assignments at the same instant,
  // which the two independent requests can easily race on. Without
  // `assignmentsReady`, that race could still silently call
  // `onAssignmentsChange` with an arbitrary default REP on a real, already-
  // launched campaign before its actual Sales Rep had loaded. Waiting for
  // `assignmentsReady` (true immediately for a brand-new draft, true only
  // once the parent's edit-mode load has actually finished otherwise) closes
  // that window entirely — the real assignment is always in hand, or known to
  // be genuinely absent, before this effect ever acts.
  useEffect(() => {
    if (autoAssignedDefault || !assignmentsReady) return;
    if (aiEmployeeAssignments.length > 0) {
      setAutoAssignedDefault(true);
      return;
    }
    if (!salesRepsLoaded || salesReps.length === 0) return;
    const def = salesReps.find(e => e.isDefault) ?? salesReps[0];
    if (def) onAssignmentsChange([{ aiEmployeeId: def._id, percentage: 100 }]);
    setAutoAssignedDefault(true);
  }, [aiEmployeeAssignments, salesReps, salesRepsLoaded, autoAssignedDefault, assignmentsReady]);
  // Always load both account lists — the campaign type is now derived from the
  // Sequence step, so the user might add an Email step at any point and we
  // want the picker to be ready without an extra round-trip.
  useEffect(() => {
    void fetchEmailAccounts();
    void fetchLinkedinAccounts();
  }, []);

  const isAssigned = (id: string) => aiEmployeeAssignments.some(a => a.aiEmployeeId === id);
  const totalPct = aiEmployeeAssignments.reduce((s, a) => s + a.percentage, 0);

  /**
   * Channels this Sales Rep still owes a sending account for — empty when
   * they're good to go. Only channels the sequence actually sends on count,
   * and a rep who borrows someone else's account through a channel fallback is
   * covered too. The server applies exactly this rule, so anything listed here
   * is a save that would come back rejected.
   */
  const missingChannelsForAie = (aieId: string): ('email' | 'linkedin')[] => {
    const covered = (channel: 'email' | 'linkedin') =>
      channelFallbacks.some(f => f.sourceAiEmployeeId === aieId && f.channel === channel);
    const missing: ('email' | 'linkedin')[] = [];
    if (requiredChannels.email && (emailAccountsByAie[aieId] ?? []).length === 0 && !covered('email'))
      missing.push('email');
    if (requiredChannels.linkedin && (linkedinAccountIdsByAie[aieId] ?? []).length === 0 && !covered('linkedin'))
      missing.push('linkedin');
    return missing;
  };

  /**
   * The quiet nudge that replaced this step's old warning-then-error sequence:
   * it appears on the rep the moment they're checked, names the one thing
   * that's missing, and stays out of the way. The error only ever arrives if
   * the user pushes forward without acting on it.
   */
  const missingAccountHint = (missing: ('email' | 'linkedin')[], suffix: string) =>
    missing.length === 0
      ? nothing
      : html`<div class="text-info flex items-center gap-1.5 text-xs">
          <span class="icon-[material-symbols--info-outline] shrink-0 text-sm"></span>
          <span>
            Please select ${missing.map(c => (c === 'email' ? 'an email account' : 'a LinkedIn account')).join(' and ')}
            for this Sales Rep${suffix}.
          </span>
        </div>`;

  const toggleAie = (id: string) => {
    if (isAssigned(id)) {
      // Only update assignments here — the parent callback also prunes orphaned
      // per-Sales-Rep account buckets in the SAME setDraft call. Calling extra
      // setters here would spread a stale draft and overwrite this change.
      const remaining = aiEmployeeAssignments.filter(a => a.aiEmployeeId !== id);
      const split = evenSplit(remaining.length);
      onAssignmentsChange(remaining.map((a, i) => ({ aiEmployeeId: a.aiEmployeeId, percentage: split[i] ?? 0 })));
    } else {
      const next = [...aiEmployeeAssignments, { aiEmployeeId: id, percentage: 0 }];
      const split = evenSplit(next.length);
      // Pre-select this rep's account for any channel the campaign sends on
      // when there is exactly one to choose — there is no decision to make
      // there, and leaving it empty is what turns "switch the Sales Rep" into
      // a rejected save. With several accounts we don't guess; the hints below
      // ask for a pick instead. Rides along in the same update (see the prop's
      // doc) and never overwrites an existing selection.
      const seed: {
        emailAccountsByAie?: Record<string, string[]>;
        linkedinAccountIdsByAie?: Record<string, string[]>;
      } = {};
      if (requiredChannels.email && (emailAccountsByAie[id] ?? []).length === 0) {
        const own = accountsForAieEmail(id);
        if (own.length === 1) seed.emailAccountsByAie = { [id]: [own[0]!.email] };
      }
      if (requiredChannels.linkedin && (linkedinAccountIdsByAie[id] ?? []).length === 0) {
        const own = accountsForAieLinkedin(id);
        if (own.length === 1) seed.linkedinAccountIdsByAie = { [id]: [own[0]!.id] };
      }
      onAssignmentsChange(
        next.map((a, i) => ({ ...a, percentage: split[i] ?? 0 })),
        seed,
      );
    }
  };

  const setAiePercentage = (id: string, value: number) => {
    // With a single AIE, the share is always 100% — ignore manual changes.
    if (aiEmployeeAssignments.length <= 1) {
      if (aiEmployeeAssignments.length === 1) {
        onAssignmentsChange(aiEmployeeAssignments.map(a => ({ ...a, percentage: 100 })));
      }
      return;
    }
    const v = Math.max(0, Math.min(100, Math.round(value)));
    // Auto-distribute the remaining (100 - v) across the OTHER AIEs so the total
    // is always exactly 100%. Distribution is even across the others; if the
    // others previously had a non-zero split we preserve their proportions.
    const others = aiEmployeeAssignments.filter(a => a.aiEmployeeId !== id);
    const remainder = 100 - v;
    const prevOthersTotal = others.reduce((s, a) => s + (a.percentage || 0), 0);
    let othersPct: number[];
    if (prevOthersTotal > 0) {
      // Proportional split, then fix rounding error on the last bucket.
      const raw = others.map(a => (a.percentage / prevOthersTotal) * remainder);
      othersPct = raw.map(n => Math.round(n));
      const diff = remainder - othersPct.reduce((s, n) => s + n, 0);
      if (othersPct.length > 0) othersPct[othersPct.length - 1]! += diff;
    } else {
      // Even split (handles the case when others were all 0).
      const base = Math.floor(remainder / others.length);
      const extra = remainder - base * others.length;
      othersPct = others.map((_, i) => base + (i < extra ? 1 : 0));
    }
    let oi = 0;
    onAssignmentsChange(
      aiEmployeeAssignments.map(a => {
        if (a.aiEmployeeId === id) return { ...a, percentage: v };
        const next = { ...a, percentage: Math.max(0, Math.min(100, othersPct[oi] ?? 0)) };
        oi++;
        return next;
      }),
    );
  };

  const equaliseSplit = () => {
    const split = evenSplit(aiEmployeeAssignments.length);
    onAssignmentsChange(aiEmployeeAssignments.map((a, i) => ({ ...a, percentage: split[i] ?? 0 })));
  };

  const toggleEmailForAie = (aieId: string, accountEmail: string) => {
    const current = emailAccountsByAie[aieId] ?? [];
    const next = current.includes(accountEmail) ? current.filter(e => e !== accountEmail) : [...current, accountEmail];
    onEmailAccountsByAieChange({ ...emailAccountsByAie, [aieId]: next });
  };
  const setAllEmailForAie = (aieId: string, emails: string[]) => {
    onEmailAccountsByAieChange({ ...emailAccountsByAie, [aieId]: emails });
  };
  const toggleLinkedinForAie = (aieId: string, accountId: string) => {
    const current = linkedinAccountIdsByAie[aieId] ?? [];
    const next = current.includes(accountId) ? current.filter(id => id !== accountId) : [...current, accountId];
    onLinkedinAccountIdsByAieChange({ ...linkedinAccountIdsByAie, [aieId]: next });
  };
  const setAllLinkedinForAie = (aieId: string, ids: string[]) => {
    onLinkedinAccountIdsByAieChange({ ...linkedinAccountIdsByAie, [aieId]: ids });
  };

  const typeCard = null;
  // (Campaign type cards were removed — the type is now derived from the
  // Sequence step. The variable is kept around the codebase only via the
  // helpers below; no UI is rendered for it any more.)
  void typeCard;

  const salesRepMultiSelector = () => {
    if (loadingSalesReps)
      return html`<div class="flex items-center gap-2 py-4"><span class="loading loading-spinner loading-sm"></span> Loading Sales Representatives...</div>`;
    if (salesReps.length === 0)
      return html`
      <div class="rounded-lg border border-base-300 bg-base-200 p-4 text-center text-sm text-base-content/50">
        No Sales Representative profiles found. Create one in Settings first.
      </div>`;

    const pctValid = aiEmployeeAssignments.length > 0 && totalPct === 100;

    return html`
      <div class="flex flex-col gap-3">
        <p class="text-base-content/70 text-xs">
          Pick one or more Sales Representatives. Each one writes their own messages and uses their own sending accounts.
          The slider controls what share of leads goes to which Sales Representative.
        </p>

        <div class="flex flex-col gap-2">
          ${salesReps.map(emp => {
            const assigned = isAssigned(emp._id);
            const pct = aiEmployeeAssignments.find(a => a.aiEmployeeId === emp._id)?.percentage ?? 0;
            // This is the "highlighted Sales Rep" the pending-changes bar and
            // the error toast both point at, so it has to read as marked
            // without reading as broken — info, not warning. Held back entirely
            // until the user tries to move on (revealAccountGaps): a rep just
            // ticked should not be marked before they can act.
            const missing = assigned && revealAccountGaps ? missingChannelsForAie(emp._id) : [];
            return html`
              <div
                class="border-base-300 rounded-lg border p-3 ${
                  assigned ? (missing.length > 0 ? 'border-info bg-info/5' : 'border-accent bg-accent/5') : ''
                }"
              >
                <label class="flex cursor-pointer items-center gap-3">
                  <input type="checkbox" class="checkbox checkbox-accent checkbox-sm"
                    .checked=${assigned}
                    @change=${() => toggleAie(emp._id)} />
                  <div class="flex flex-1 flex-col">
                    <div class="flex items-center gap-2">
                      <span class="font-medium">${emp.name}</span>
                      ${emp.isDefault ? html`<span class="badge badge-accent badge-xs">Default</span>` : nothing}
                    </div>
                    <span class="text-base-content/60 text-xs">${emp.role} at ${emp.company}</span>
                  </div>
                  ${assigned ? html`<span class="font-mono text-sm font-semibold text-accent">${pct}%</span>` : nothing}
                </label>
                ${
                  assigned
                    ? html`
                  <div class="mt-2 flex items-center gap-3">
                    <input type="range" min="0" max="100" step="1" class="range range-accent range-xs flex-1"
                      .value=${String(aiEmployeeAssignments.length === 1 ? 100 : pct)}
                      ?disabled=${aiEmployeeAssignments.length === 1}
                      @input=${(e: Event) => setAiePercentage(emp._id, Number((e.target as HTMLInputElement).value))} />
                    <input type="number" min="0" max="100" class="input input-bordered input-xs w-16"
                      .value=${String(aiEmployeeAssignments.length === 1 ? 100 : pct)}
                      ?disabled=${aiEmployeeAssignments.length === 1}
                      @change=${(e: Event) => setAiePercentage(emp._id, Number((e.target as HTMLInputElement).value))} />
                    ${aiEmployeeAssignments.length === 1 ? html`<span class="text-base-content/50 text-xs">Only Sales Rep — always 100%</span>` : nothing}
                  </div>
                  ${missing.length > 0 ? html`<div class="mt-2">${missingAccountHint(missing, ' below')}</div>` : nothing}
                `
                    : nothing
                }
              </div>
            `;
          })}
        </div>

        ${
          aiEmployeeAssignments.length > 1
            ? html`
          <div class="flex items-center justify-between rounded-lg ${pctValid ? 'bg-success/10' : 'bg-warning/10'} px-3 py-2 text-sm">
            <span>
              Total: <span class="font-mono font-semibold">${totalPct}%</span>
              ${pctValid ? html` <span class="text-success">✓</span>` : html` <span class="text-warning">— must equal 100%</span>`}
            </span>
            <button class="btn btn-ghost btn-xs" @click=${equaliseSplit}>
              <span class="icon-[material-symbols--balance]"></span> Equal split
            </button>
          </div>
        `
            : nothing
        }
      </div>
    `;
  };

  const accountsForAieEmail = (aieId: string) => allEmailAccounts.filter(a => a.aiEmployeeId === aieId);
  const accountsForAieLinkedin = (aieId: string) => allLinkedinAccounts.filter(a => a.aiEmployeeId === aieId);

  const emailBlockForAie = (aieId: string) => {
    if (loadingEmail)
      return html`<div class="flex items-center gap-2 py-3"><span class="loading loading-spinner loading-xs"></span> Loading...</div>`;
    if (emailError)
      return html`<div class="alert alert-warning text-sm"><span class="icon-[material-symbols--warning] text-lg"></span> ${emailError}</div>`;
    const accounts = accountsForAieEmail(aieId);
    const selected = emailAccountsByAie[aieId] ?? [];
    if (accounts.length === 0)
      return html`
      <p class="text-base-content/60 text-xs">No email accounts assigned to this Sales Representative. Connect one in Settings.</p>`;
    return html`
      <div class="flex flex-col gap-1.5">
        ${accounts.map(
          account => html`
          <label class="border-base-300 hover:bg-base-200 flex cursor-pointer items-center gap-3 rounded border px-3 py-2 transition">
            <input type="checkbox" class="checkbox checkbox-primary checkbox-xs"
              .checked=${selected.includes(account.email)}
              @change=${() => toggleEmailForAie(aieId, account.email)} />
            <div class="flex flex-col">
              <span class="text-sm font-medium">${account.name}</span>
              <span class="text-base-content/60 text-xs">${account.email}</span>
            </div>
          </label>
        `,
        )}
      </div>
      ${
        accounts.length > 1
          ? html`
        <div class="mt-2 flex gap-2">
          <button class="btn btn-outline btn-xs" @click=${() =>
            setAllEmailForAie(
              aieId,
              accounts.map(a => a.email),
            )}>Select all</button>
          <button class="btn btn-outline btn-xs" @click=${() => setAllEmailForAie(aieId, [])}>Clear</button>
        </div>
      `
          : nothing
      }
    `;
  };

  const linkedinBlockForAie = (aieId: string) => {
    if (loadingLinkedin)
      return html`<div class="flex items-center gap-2 py-3"><span class="loading loading-spinner loading-xs"></span> Loading...</div>`;
    if (linkedinError)
      return html`<div class="alert alert-warning text-sm"><span class="icon-[material-symbols--warning] text-lg"></span> ${linkedinError}</div>`;
    const accounts = accountsForAieLinkedin(aieId);
    const selected = linkedinAccountIdsByAie[aieId] ?? [];
    if (accounts.length === 0)
      return html`
      <p class="text-base-content/60 text-xs">No LinkedIn accounts assigned to this Sales Representative. Connect one in Settings.</p>`;
    return html`
      <div class="flex flex-col gap-1.5">
        ${accounts.map(account => {
          // The seat, at the moment the account is actually chosen for a
          // campaign — a free seat can only personalise a limited number of
          // invitations per month, and that is a fact about the pick, not a
          // problem with it. No warning styling, no block.
          const seat = describeSeat(account.premiumFeatures);
          return html`
          <label class="border-base-300 hover:bg-base-200 flex cursor-pointer items-center gap-3 rounded border px-3 py-2 transition">
            <input type="checkbox" class="checkbox checkbox-primary checkbox-xs"
              .checked=${selected.includes(account.id)}
              @change=${() => toggleLinkedinForAie(aieId, account.id)} />
            <span class="text-sm font-medium">${account.name}</span>
            <span class=${seatBadgeClass(seat.kind)} title=${seat.title}>${seat.label}</span>
          </label>
        `;
        })}
      </div>
      ${
        accounts.length > 1
          ? html`
        <div class="mt-2 flex gap-2">
          <button class="btn btn-outline btn-xs" @click=${() =>
            setAllLinkedinForAie(
              aieId,
              accounts.map(a => a.id),
            )}>Select all</button>
          <button class="btn btn-outline btn-xs" @click=${() => setAllLinkedinForAie(aieId, [])}>Clear</button>
        </div>
      `
          : nothing
      }
    `;
  };

  const accountsCard = () => {
    if (aiEmployeeAssignments.length === 0) {
      return html`<div class="text-base-content/50 py-3 text-sm">Pick at least one Sales Representative above.</div>`;
    }
    // Both channels are always rendered — the user picks accounts up-front and
    // the actually used channels are determined later by the Sequence builder.
    return html`
      <div class="flex flex-col gap-3">
        ${aiEmployeeAssignments.map(a => {
          const emp = salesReps.find(e => e._id === a.aiEmployeeId);
          if (!emp) return nothing;
          // The hint repeats here because this is where the pick actually
          // happens — the one in the rep list above only says "below".
          // Suppressed when the rep owns no account for that channel at all:
          // the block's own "connect one in Settings" line is the real next
          // step then, and "please select" would be asking for the impossible.
          // Like the rep-list hint, held back until the user tries to move on.
          const missing = revealAccountGaps ? missingChannelsForAie(a.aiEmployeeId) : [];
          return html`
            <div class="border-base-300 rounded-lg border p-3 ${missing.length > 0 ? 'border-info' : ''}">
              <div class="mb-2 flex items-center gap-2">
                <span class="bg-accent/10 text-accent flex h-7 w-7 items-center justify-center rounded">
                  <span class="icon-[material-symbols--smart-toy] text-base"></span>
                </span>
                <span class="font-medium">${emp.name}</span>
                <span class="badge badge-accent badge-xs">${a.percentage}% of leads</span>
              </div>
              ${html`
                <div class="mt-2">
                  <div class="text-base-content/70 mb-1 flex items-center gap-1.5 text-xs font-semibold">
                    <span class="icon-[material-symbols--mail] text-secondary text-sm"></span> Email accounts
                  </div>
                  ${
                    missing.includes('email') && accountsForAieEmail(a.aiEmployeeId).length > 0
                      ? html`<div class="mb-1.5">${missingAccountHint(['email'], '')}</div>`
                      : nothing
                  }
                  ${emailBlockForAie(a.aiEmployeeId)}
                </div>
              `}
              ${html`
                <div class="mt-3">
                  <div class="text-base-content/70 mb-1 flex items-center gap-1.5 text-xs font-semibold">
                    <span class="icon-[material-symbols--share] text-info text-sm"></span> LinkedIn accounts
                  </div>
                  ${
                    missing.includes('linkedin') && accountsForAieLinkedin(a.aiEmployeeId).length > 0
                      ? html`<div class="mb-1.5">${missingAccountHint(['linkedin'], '')}</div>`
                      : nothing
                  }
                  ${linkedinBlockForAie(a.aiEmployeeId)}
                </div>
              `}
            </div>
          `;
        })}
      </div>
    `;
  };

  return html`
    <div class="flex flex-col gap-6">
      <div class="ndi-expand card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center gap-3">
            <span class="bg-accent/10 text-accent flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--groups] text-xl"></span>
            </span>
            <div>
              <h2 class="card-title text-lg">Sales Representatives & Split</h2>
              <p class="text-base-content/70 text-sm">Assign one or more Sales Representatives and choose how leads are split between them.</p>
            </div>
          </div>
          <div class="mt-4">${salesRepMultiSelector()}</div>
        </div>
      </div>

      <div class="ndi-expand card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center gap-3">
            <span class="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--hub] text-xl"></span>
            </span>
            <div>
              <h2 class="card-title text-lg">Email & LinkedIn Accounts per Sales Representative</h2>
              <p class="text-base-content/70 text-sm">
                Pick the accounts each Sales Representative will use. The actual channels are
                determined by the steps you add in the Sequence builder — unused accounts here
                are simply ignored.
              </p>
            </div>
          </div>
          <div class="mt-4">${accountsCard()}</div>
        </div>
      </div>

      <div class="ndi-expand card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center gap-3">
            <span class="bg-warning/10 text-warning flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--hub] text-xl"></span>
            </span>
            <div>
              <h2 class="card-title text-lg">CRM Sync</h2>
              <p class="text-base-content/70 text-sm">
                Decide how this campaign's leads are mirrored into your connected CRM (e.g. HubSpot).
              </p>
            </div>
          </div>
          <div class="mt-4">
            <label class="flex cursor-pointer items-start gap-3 rounded-lg border border-base-300 bg-base-100 p-4">
              <input
                type="checkbox"
                class="toggle toggle-primary mt-0.5"
                .checked=${!!crmAutoSync}
                @change=${(e: Event) => onCrmAutoSyncChange((e.target as HTMLInputElement).checked)}
              />
              <div class="flex-1">
                <div class="font-medium">Add &amp; Update all Leads in CRM</div>
                <p class="text-base-content/60 mt-1 text-sm">
                  ${
                    crmAutoSync
                      ? 'ON: every lead (including CSV imports) is automatically pushed to the CRM and kept up to date after each sync.'
                      : 'OFF: CSV-only leads are NOT pushed to the CRM. Leads that originated from the CRM are still kept in sync.'
                  }
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  `;
});
